import { useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { useTranslationState } from '@/modules/tryIt/components/ModalTryIt/useTranslationState';
import TranslateSelect from '@/modules/tryIt/components/TranslateSelect';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';

import { useSessionTranslation } from '../../hooks/useSessionTranslation';
import { replayLanguageChanged } from '../../redux/actions';
import { selectTryItSessionId } from '../../redux/selectors';

export const TranslateDropdown = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sessionId = useSelector(selectTryItSessionId);

  const {
    isEnabled,
    selectedLanguage,
    currentLanguage,
    languageOptions,
    handleToggle,
    handleLanguageSelect,
    disableOnError,
  } = useTranslationState(sessionId);

  const { isLoading, isError, status } = useSessionTranslation(sessionId, currentLanguage);

  const setReplayLanguage = useCallback(
    (language: string | undefined) => dispatch(replayLanguageChanged({ language })),
    [dispatch]
  );

  // Mirror the local UI state into the modal state machine so other components
  // (timeline parser, replay-mode panels) can read the active language from
  // Redux instead of duplicating `useTranslationState`.
  useEffect(() => {
    setReplayLanguage(currentLanguage);
  }, [currentLanguage, setReplayLanguage]);

  useEffect(() => {
    disableOnError(isError);
  }, [isError, disableOnError]);

  // Without this a failed translation just flips the toggle back off, which
  // reads as "translation never happens".
  useEffect(() => {
    if (!currentLanguage) {
      return;
    }
    if (status === 'success') {
      toast.success(t('try_it.logs_translated_toast'));
      trackEvent(EventName.ConversationTranslateSuccess, {
        session_id: sessionId,
        language: currentLanguage,
      });
    }
    if (status === 'error') {
      toast.error(t('try_it.logs_translated_error_toast'));
      trackEvent(EventName.ConversationTranslateError, {
        session_id: sessionId,
        language: currentLanguage,
      });
    }
  }, [status, currentLanguage, sessionId, t]);

  return (
    <TranslateSelect
      options={languageOptions}
      value={selectedLanguage ?? ''}
      isLoading={isLoading}
      isButtonSelected={isEnabled}
      onButtonClick={handleToggle}
      onValueSelect={handleLanguageSelect}
    />
  );
};
